import type { WatchQueryFetchPolicy } from "@apollo/client";
import {
  ColumnDef,
  ColumnPinningState,
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Text } from "@chakra-ui/react";

import { DocumentType, getFragmentData, gql } from "__generated__/gql";
import {
  useChakraReactTableProps,
  usePrepApolloData,
} from "components/Shared/ChakraReactTable/utils";
import { useAdminOrBrandContext } from "utils/apollo/hooks/adminOrBrand";
import usePagination, {
  usePaginationReset,
} from "components/Shared/ChakraReactTable/usePagination";
import useGQLSorting, {
  GQLOrderByEnumsById,
} from "components/Shared/ChakraReactTable/useGQLSorting";
import {
  formatAddressCell,
  formatJustifiedDateTimeCell,
} from "components/Shared/ChakraReactTable/formatters";
import useStickyHeader from "components/Shared/ChakraReactTable/useStickyHeader";
import {
  DistributionCenterFilter,
  DistributionCentersOrderBy,
} from "__generated__/gql/graphql";
import useFixedLayout from "components/Shared/ChakraReactTable/useFixedLayout";
import useSelectableRow from "components/Shared/ChakraReactTable/useSelectableRow";
import IsCustomBadge from "components/Shared/OrderingPlatformDirectory/IsCustomBadge";
import { useQuery } from "utils/apollo/hooks";
import FullPageTable, {
  FullPageTableProps,
} from "components/Shared/ChakraReactTable/FullPageTable";
import DistributionCenterName from "components/Shared/OrderingPlatformDirectory/DistributionCenters/DistributionCenterName";
import CopyableCode from "components/Shared/CopyableCode";
import { NA } from "components/Shared/EmptyPlaceholder";
import useArchivedRowProps from "components/Shared/ChakraReactTable/useArchivedRowProps";

export const fragment = gql(/* GraphQL */ `
  fragment DistributionCentersDirectoryTableRow on DistributionCenter {
    id
    name
    fullDisplayName
    code
    createdAt
    isArchived
    orderingPlatform {
      id
      name
      forBrandId
      ...CustomerIsCustomBadge
    }
    address {
      id
      ...AddressComponentFields
    }
    phoneNumber {
      id
      phoneFormatted(format: NATIONAL)
    }
    ...DistributionCenterNameFragment
  }
`);

const query = gql(/* GraphQL */ `
  query DistributionCentersDirectoryTable(
    $filter: DistributionCenterFilter
    $orderBy: [DistributionCentersOrderBy!] = [CREATED_AT_ASC]
    $limit: Int = 10
    $offset: Int = 0
  ) {
    distributionCenters(
      filter: $filter
      first: $limit
      offset: $offset
      orderBy: $orderBy
    ) {
      totalCount
      nodes {
        id
        ...DistributionCentersDirectoryTableRow
      }
    }
  }
`);

export type Datum = DocumentType<typeof fragment>;

const col = createColumnHelper<Datum>();

export const ORDERING_PLATFORM_COLUMNS = [
  col.accessor((r) => r.orderingPlatform?.name, {
    id: "orderingPlatformName",
    header: "Customer",
    size: 250,
  }),
  col.accessor((r) => !!r.orderingPlatform?.forBrandId, {
    id: "isCustom",
    header: "Custom?",
    cell: (cell) =>
      cell.row.original.orderingPlatform && (
        <IsCustomBadge orderingPlatform={cell.row.original.orderingPlatform} />
      ),
    size: 120,
    enableSorting: false,
    meta: { props: { textAlign: "center" } },
  }),
];

export const DISTRIBUTION_CENTER_COLUMNS = [
  col.accessor((r) => r.fullDisplayName, {
    id: "name",
    header: "Name",
    cell: (cell) => (
      <DistributionCenterName distributionCenter={cell.row.original} />
    ),
    size: 175,
  }),
  col.accessor((r) => r.code, {
    id: "code",
    header: "Code",
    cell: (cell) => {
      const code = cell.getValue();
      if (!code) return <NA />;
      return <CopyableCode code={code} isTruncated maxW="100%" />;
    },
    size: 125,
  }),
  col.accessor((r) => r.address, {
    id: "address",
    header: "Address",
    cell: formatAddressCell,
    size: 220,
    enableSorting: false,
    meta: { cellProps: { fontSize: ".9em", lineHeight: "normal" } },
  }),
  col.accessor((r) => r.phoneNumber?.phoneFormatted, {
    id: "phoneNumber",
    header: "Phone Number",
    size: 155,
  }),
  col.accessor((r) => r.createdAt, {
    id: "createdAt",
    header: "Added",
    cell: (cell) => formatJustifiedDateTimeCell(cell),
    meta: {
      props: { textAlign: "right" },
      cellProps: { fontSize: ".85em", lineHeight: "normal" },
    },
    size: 115,
  }),
];

export const DEFAULT_COLUMNS = [
  ...DISTRIBUTION_CENTER_COLUMNS.slice(0, -1),
  ...ORDERING_PLATFORM_COLUMNS,
  ...DISTRIBUTION_CENTER_COLUMNS.slice(-1),
];

export const DEFAULT_PINNED_COLUMNS: ColumnPinningState = { left: ["name"] };

export const orderByEnumsById: GQLOrderByEnumsById<DistributionCentersOrderBy> =
  {
    name: [
      DistributionCentersOrderBy.FULL_DISPLAY_NAME_ASC,
      DistributionCentersOrderBy.FULL_DISPLAY_NAME_DESC,
    ],
    code: [
      DistributionCentersOrderBy.CODE_ASC,
      DistributionCentersOrderBy.CODE_DESC,
    ],
    phoneNumber: [
      DistributionCentersOrderBy.PHONE_NUMBER__PHONE_ASC,
      DistributionCentersOrderBy.PHONE_NUMBER__PHONE_DESC,
    ],
    orderingPlatformName: [
      DistributionCentersOrderBy.ORDERING_PLATFORM__NAME_ASC,
      DistributionCentersOrderBy.ORDERING_PLATFORM__NAME_DESC,
    ],
    createdAt: [
      DistributionCentersOrderBy.CREATED_AT_ASC,
      DistributionCentersOrderBy.CREATED_AT_DESC,
    ],
  };

export type DistributionCentersDirectoryTableProps = Pick<
  FullPageTableProps<Datum>,
  "scrollContainerProps" | "tableContainerProps" | "tableControlsFooterProps"
> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns?: ColumnDef<Datum, any>[];
  defaultPageSize?: number;
  filter?: DistributionCenterFilter;
  isAdmin?: boolean;
  isReady?: boolean;
  nextFetchPolicy?: WatchQueryFetchPolicy;
  onSelectedChange?: (id: UUID) => void;
  pinnedColumns?: ColumnPinningState;
  selected?: UUID | null;
};

export default function DistributionCentersDirectoryTable({
  columns = DEFAULT_COLUMNS,
  defaultPageSize = 10,
  filter,
  isAdmin,
  isReady = true,
  nextFetchPolicy,
  onSelectedChange,
  pinnedColumns: columnPinning = DEFAULT_PINNED_COLUMNS,
  selected,
  scrollContainerProps,
  tableContainerProps,
  tableControlsFooterProps,
}: DistributionCentersDirectoryTableProps) {
  const {
    pagination,
    onPaginationChange,
    limitOffset,
    getPageCount,
    setPageIndex,
  } = usePagination({ pageSize: defaultPageSize });

  const { sorting, onSortingChange, orderBy } = useGQLSorting(
    orderByEnumsById,
    [{ id: "createdAt", desc: false }],
  );

  const [context, isContextReady] = useAdminOrBrandContext(isAdmin);

  const isPendingFilterReset = usePaginationReset(
    filter,
    setPageIndex,
    pagination.pageIndex,
  );

  const isPendingSortingReset = usePaginationReset(
    sorting,
    setPageIndex,
    pagination.pageIndex,
  );

  const {
    data: rawData,
    loading: isLoading,
    refetch,
  } = useQuery(query, {
    context,
    nextFetchPolicy,
    skip:
      !isReady ||
      !isContextReady ||
      isPendingFilterReset ||
      isPendingSortingReset,
    variables: { filter, orderBy, ...limitOffset },
  });

  const { nodes, totalCount } = rawData?.distributionCenters || {};

  const tableInstance = useReactTable({
    columns,
    data: usePrepApolloData(getFragmentData(fragment, nodes)),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualSorting: true,
    enableSorting: true,
    state: { columnPinning, pagination, sorting },
    pageCount: getPageCount(totalCount),
  });

  const selectableRowProps = useSelectableRow<Datum>({
    selected,
    onSelectedChange: onSelectedChange ?? (() => {}),
  });

  const tableProps = useChakraReactTableProps<Datum>([
    useStickyHeader(),
    useFixedLayout(tableInstance),
    useArchivedRowProps((row) => row.original.isArchived),
    onSelectedChange ? selectableRowProps : {},
    {
      tableProps: { fontSize: "sm", variant: "condensed" },
      tableBodyProps: { sx: { "> tr > td": { py: 2 } } },
    },
  ]);

  return (
    <FullPageTable
      tableInstance={tableInstance}
      {...tableProps}
      {...{
        scrollContainerProps,
        tableContainerProps,
        tableControlsFooterProps,
      }}
      isLoading={isLoading || !isReady || typeof window === "undefined"}
      noRowsPlaceholder={<Text>No distribution centers found.</Text>}
      totalCount={totalCount}
      resultTypeLabel="distribution centers"
      onRefresh={refetch}
    />
  );
}
